import { Button } from '@attive/ui/button'
import { withSentry } from '@sentry/remix'
import { NuqsAdapter } from 'nuqs/adapters/react'
import { useEffect } from 'react'
import { Toaster } from 'react-hot-toast'
import {
	data,
	type HeadersFunction,
	Link,
	Links,
	type LinksFunction,
	type LoaderFunctionArgs,
	Meta,
	type MetaFunction,
	Outlet,
	redirect,
	Scripts,
	ScrollRestoration,
	useLoaderData,
	useLocation,
	useParams,
} from 'react-router'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { Logo } from './components/Logo.tsx'
import { EpicProgress } from './components/progress-bar.tsx'
import { useToast } from './components/toaster.tsx'
import { UserMenu } from './components/UserMenu'
import { ThemeSwitch, useTheme } from './routes/resources+/theme-switch.tsx'
import { initializeOpenPanel } from './services/analytics/openpanel.client.ts'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { getUserId, logout } from './utils/auth.server.ts'
import { ClientHintCheck, getHints } from './utils/client-hints.tsx'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import { combineHeaders, getDomainUrl } from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { getTheme, type Theme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import { useOptionalUser } from './utils/user.ts'

export const links: LinksFunction = () => {
	return [
		// Google Fonts preconnect links
		{ rel: 'preconnect', href: 'https://fonts.googleapis.com' },
		{
			rel: 'preconnect',
			href: 'https://fonts.gstatic.com',
		},
		// Google Fonts stylesheet
		{
			rel: 'stylesheet',
			href: 'https://fonts.googleapis.com/css2?family=Exo:wght@400;500;600;700&family=Fira+Code:wght@300;400;500;600;700&family=Inter:wght@100;200;300;400;500;600;700;800;900&display=swap',
		},
		{
			rel: 'stylesheet',
			href: 'https://fonts.googleapis.com/css2?family=JetBrains+Mono:wght@400;500;600;700&family=Outfit:wght@300;400;500;600;700&family=Work+Sans:ital,wght@0,400;0,500;0,600;0,700;1,400&display=swap',
			media: 'print',
		},
		{
			rel: 'icon',
			type: 'image/png',
			href: '/favicons/favicon-96x96.png',
			sizes: '96x96',
		},
		{
			rel: 'apple-touch-icon',
			href: '/favicons/apple-touch-icon.png',
			sizes: '180x180',
		},
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	return [
		{ title: data ? 'Attive' : 'Error | Attive' },
		{ name: 'description', content: `Your own captain's log` },
	]
}

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	// Handle root path redirect
	const url = new URL(request.url)

	if (url.pathname === '/' && userId) {
		throw redirect('/workspaces')
	}

	// Redirect unauthenticated users to login page
	if (
		!userId &&
		!request.url.includes('/login') &&
		!request.url.includes('/signup') &&
		!request.url.includes('/reset-password') &&
		!request.url.includes('/forgot-password') &&
		!request.url.includes('/verify') &&
		!request.url.includes('/auth/') &&
		!request.url.includes('/onboarding') &&
		!request.url.includes('/accept-invitation/') &&
		!request.url.includes('/export/')
	) {
		throw redirect('/login')
	}

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							email: true,
							isAdmin: true,
							image: { select: { id: true } },
							quickQuestions: true,
							userNotes: true,
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
							workspaces: {
								select: {
									role: true,
									workspaceId: true,
								},
							},
							featureFlags: true,
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const honeyProps = honeypot.getInputProps()

	return data(
		{
			user,
			requestInfo: {
				hints: getHints(request),
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: getEnv(),
			toast,
			honeyProps,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

function Document({
	children,
	nonce,
	theme = 'light',
	env,
	allowIndexing = true,
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: ReturnType<typeof getEnv>
	allowIndexing?: boolean
}) {
	return (
		<html lang="en" className={`${theme} h-full`}>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta name="viewport" content="width=device-width,initial-scale=1" />
				<meta name="mobile-web-app-capable" content="yes" />
				<meta name="theme-color" content="#09090B" />
				{allowIndexing ? null : (
					<meta name="robots" content="noindex, nofollow" />
				)}
				<Links />
			</head>
			<body className="bg-background text-foreground">
				{children}
				<script
					nonce={nonce}
					// biome-ignore lint/security/noDangerouslySetInnerHtml: n/a
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}
export type RootLoaderType = typeof loader

function App() {
	const data = useLoaderData<RootLoaderType>()
	const nonce = useNonce()
	const user = useOptionalUser()
	const theme = useTheme()
	const location = useLocation()
	const { slug } = useParams()

	const isWorkspacePage =
		location.pathname.startsWith('/workspaces/') &&
		!location.pathname.endsWith('workspaces/new')

	const isExportPage = location.pathname.startsWith('/export/')

	const isAuthPage =
		location.pathname.startsWith('/login') ||
		location.pathname.startsWith('/signup') ||
		location.pathname.startsWith('/forgot-password') ||
		location.pathname.startsWith('/reset-password') ||
		location.pathname.startsWith('/verify') ||
		location.pathname.startsWith('/auth/')

	const isOnboardingPage = location.pathname.startsWith('/onboarding/')

	const allowIndexing = data.ENV.ALLOW_INDEXING !== 'false'
	useToast(data.toast)

	// Initialize OpenPanel analytics
	useEffect(() => {
		initializeOpenPanel(data.user, slug)
	}, [data.user, slug])
	return (
		<Document
			nonce={nonce}
			theme={theme}
			allowIndexing={allowIndexing}
			env={data.ENV}
		>
			<NuqsAdapter>
				<div className="flex h-screen flex-col justify-between">
					{!isWorkspacePage &&
						!isExportPage &&
						!isAuthPage &&
						!isOnboardingPage && (
							<header className="flex h-14 items-center px-4 py-6 lg:px-8 lg:py-8">
								<Logo size="medium" />
								<nav className="ml-auto flex items-center gap-4">
									<ThemeSwitch
										userPreference={data.requestInfo.userPrefs.theme}
									/>
									{user ? (
										<UserMenu />
									) : (
										<Button
											asChild
											variant="default"
											size="lg"
											className="inline-flex"
										>
											<Link to="/login">Log In</Link>
										</Button>
									)}
								</nav>
							</header>
						)}

					<div className="flex-1">
						<Outlet />
					</div>
				</div>

				<Toaster
					position="top-right"
					toastOptions={{
						className: '',
						style: {
							background: theme === 'dark' ? '#18181b' : '#fff',
							color: theme === 'dark' ? '#fafafa' : '#09090b',
							border:
								theme === 'dark' ? '1px solid #27272a' : '1px solid #e4e4e7',
							borderRadius: '0.5rem',
							fontSize: '0.875rem',
							padding: '0.75rem 1rem',
							boxShadow:
								theme === 'dark'
									? '0 10px 15px -3px rgba(0, 0, 0, 0.3), 0 4px 6px -2px rgba(0, 0, 0, 0.2)'
									: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
						},
						success: {
							iconTheme: {
								primary: '#10b981',
								secondary: theme === 'dark' ? '#18181b' : '#fff',
							},
						},
						error: {
							iconTheme: {
								primary: '#ef4444',
								secondary: theme === 'dark' ? '#18181b' : '#fff',
							},
						},
						loading: {
							iconTheme: {
								primary: '#3b82f6',
								secondary: theme === 'dark' ? '#18181b' : '#fff',
							},
						},
					}}
				/>
				<EpicProgress />
			</NuqsAdapter>
		</Document>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders)

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
